import React, { useEffect, useRef, useState } from 'react';
import { Trash2, Upload, X } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '../../lib/utils';
import { createPortal } from 'react-dom';
import { Topic } from '../../types/topic';
import { extractTextFromPDF } from '../../lib/pdf';

interface TopicContextModalProps {
  isOpen: boolean;
  onClose: () => void;
  topic: Topic;
  onSubmit: (context: string) => void;
}

export function TopicContextModal({ isOpen, onClose, topic, onSubmit }: TopicContextModalProps) {
  const [context, setContext] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isProcessingFile, setIsProcessingFile] = useState(false);

  useEffect(() => {
    if (topic) {
      setContext(topic.context || '');
      setIsEditing(!topic.context);
    }
  }, [topic, isOpen]);

  const handleFileUpload = async (file: File) => {
    setIsProcessingFile(true);
    setUploadError(null);

    try {
      const extractedText = await extractTextFromPDF(file);

      // Append the extracted text to existing context
      const newContext = context ? `${context}\n\n${extractedText}` : extractedText;
      setContext(newContext);
    } catch (error) {
      console.error('Error processing PDF:', error);
      setUploadError('Failed to process PDF file. Please try again.');
    } finally {
      setIsProcessingFile(false);
    }
  };

  if (!isOpen) return null;

  const modal = (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />
      <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Topic Context</h2>
            <div className="flex items-center gap-2">
              {!isEditing && context && (
                <>
                  <button
                    onClick={() => setIsEditing(true)}
                    className="px-3 py-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => {
                      setContext('');
                      onSubmit('');
                      setIsEditing(false);
                    }}
                    className="px-3 py-1.5 text-sm text-red-600 hover:text-red-700 font-medium flex items-center gap-1"
                  >
                    <Trash2 size={16} />
                    <span>Clear</span>
                  </button>
                </>
              )}
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {isEditing ? (
              <form
                onSubmit={e => {
                  e.preventDefault();
                  onSubmit(context);
                  setIsEditing(false);
                }}
                className="p-4 space-y-4"
              >
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block font-medium text-gray-700">
                      Context (Markdown supported)
                    </label>
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                      disabled={isProcessingFile}
                    >
                      <Upload size={16} className={cn(isProcessingFile && 'animate-spin')} />
                      <span>{isProcessingFile ? 'Processing...' : 'Upload PDF'}</span>
                    </button>
                  </div>
                  <textarea
                    value={context}
                    onChange={e => setContext(e.target.value)}
                    className="w-full h-[calc(100vh-400px)] min-h-[300px] border border-gray-300 rounded-lg px-4 py-2 text-base outline-none font-montserrat"
                    placeholder="Add topic context here... You can write directly or upload a PDF file. Markdown formatting is supported."
                  />
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf"
                    onChange={async e => {
                      const file = e.target.files?.[0];
                      if (!file) return;

                      await handleFileUpload(file);
                      if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                      }
                    }}
                    className="hidden"
                  />
                </div>

                {uploadError && (
                  <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3 flex items-center justify-between">
                    <span>{uploadError}</span>
                    <button
                      type="button"
                      onClick={() => setUploadError(null)}
                      className="text-red-400 hover:text-red-600"
                    >
                      <X size={16} />
                    </button>
                  </div>
                )}

                <div className="flex justify-end gap-3">
                  {context && (
                    <button
                      type="button"
                      onClick={() => setIsEditing(false)}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg shadow-btn hover:bg-gray-200 hover:shadow-btn-hover transition-all"
                    >
                      Cancel
                    </button>
                  )}
                  <button
                    type="submit"
                    className={cn(
                      'px-4 py-2 bg-blue-500 text-white rounded-lg shadow-btn',
                      'hover:bg-blue-600 hover:shadow-btn-hover transition-all'
                    )}
                  >
                    Save Context
                  </button>
                </div>
              </form>
            ) : context ? (
              <div className="p-6">
                <div className="prose max-w-none">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{context}</ReactMarkdown>
                </div>
              </div>
            ) : (
              <div className="p-6 text-center text-gray-500">
                <p>No context has been added yet.</p>
                <button
                  onClick={() => setIsEditing(true)}
                  className="mt-2 text-blue-600 hover:text-blue-700 font-medium"
                >
                  Add Context
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(modal, document.body);
}
